import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Facility, FleetVehicle } from "./fleetData";

const center: L.LatLngTuple = [18.94, 73.025];
const colors = { running: "#458e62", idle: "#c07a30", halted: "#bb4340", unknown: "#7a8597" };
interface Props { vehicles: FleetVehicle[]; facilities: Facility[]; selected?: FleetVehicle; onSelect: (id: string) => void; trails: Record<string, L.LatLngTuple[]>; }
export default function FleetMap({ vehicles, facilities, selected, onSelect, trails }: Props) {
  const element = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const vehicleLayer = useRef<L.LayerGroup | null>(null);
  const facilityLayer = useRef<L.LayerGroup | null>(null);
  const trailLayer = useRef<L.Polyline | null>(null);
  const [showFacilities, setShowFacilities] = useState(true);
  const [showTrail, setShowTrail] = useState(true);
  const [tileError, setTileError] = useState(false);
  const fitted = useRef(false);
  useEffect(() => {
    if (!element.current) return;
    const instance = L.map(element.current, { zoomControl: false, scrollWheelZoom: true }).setView(center, 12);
    map.current = instance;
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).on("tileerror", () => setTileError(true)).addTo(instance);
    L.control.zoom({ position: "bottomright" }).addTo(instance);
    vehicleLayer.current = L.layerGroup().addTo(instance);
    facilityLayer.current = L.layerGroup().addTo(instance);
    const observer = new ResizeObserver(() => instance.invalidateSize());
    observer.observe(element.current);
    return () => { observer.disconnect(); instance.remove(); map.current = null; fitted.current = false; };
  }, []);
  useEffect(() => {
    const layer = vehicleLayer.current;
    if (!layer || !map.current) return;
    layer.clearLayers();
    const positions: L.LatLngTuple[] = [];
    vehicles.forEach(vehicle => {
      if (vehicle.lat === undefined || vehicle.lng === undefined) return;
      const position: L.LatLngTuple = [vehicle.lat, vehicle.lng];
      positions.push(position);
      const label = document.createElement("span");
      label.textContent = vehicle.plate + (vehicle.unreachable ? " · Last known location · Current location unavailable" : "");
      const marker = L.marker(position, {
        keyboard: true, title: vehicle.plate + ", " + (vehicle.unreachable ? "Current location unavailable" : vehicle.status),
        icon: L.divIcon({ className: "fleet-vehicle-marker" + (vehicle.unreachable ? " unreachable" : "") + (selected?.id === vehicle.id ? " selected" : ""), html: '<span style="--vehicle-color:' + (vehicle.unreachable ? colors.unknown : colors[vehicle.status]) + '"><i></i></span>', iconSize: [24, 16], iconAnchor: [12, 8] }),
      }).bindTooltip(label, { direction: "top", offset: [0, -9] }).on("click", () => onSelect(vehicle.id)).addTo(layer);
      if (selected?.id === vehicle.id) marker.openTooltip();
    });
    if (!fitted.current && positions.length) {
      map.current.fitBounds(L.latLngBounds(positions), { padding: [70, 90], maxZoom: 14 });
      fitted.current = true;
    }
  }, [vehicles, selected?.id, onSelect]);
  useEffect(() => {
    const layer = facilityLayer.current;
    if (!layer) return;
    layer.clearLayers();
    if (!showFacilities) return;
    facilities.forEach(facility => {
      const label = document.createElement("span"); label.textContent = facility.name;
      L.circleMarker([facility.lat, facility.lng], { radius: 4, color: "#fff", weight: 2, fillColor: "#6c619a", fillOpacity: 1 })
        .bindTooltip(label, { permanent: true, direction: "right", className: "fleet-facility-label" }).addTo(layer);
      if (facility.radius) L.circle([facility.lat, facility.lng], { radius: facility.radius, color: "#6c619a", weight: 1, fillOpacity: .07 }).addTo(layer);
    });
  }, [facilities, showFacilities]);
  useEffect(() => {
    if (!map.current) return;
    trailLayer.current?.remove();
    const points = selected ? trails[selected.id] : undefined;
    if (showTrail && points && points.length > 1) trailLayer.current = L.polyline(points, { color: "#315fae", weight: 3 }).addTo(map.current);
  }, [selected, showTrail, trails]);
  useEffect(() => {
    if (selected?.lat !== undefined && selected.lng !== undefined) map.current?.panTo([selected.lat, selected.lng]);
  }, [selected?.id, selected?.lat, selected?.lng]);
  const fit = () => {
    const points = vehicles.filter(v => v.lat !== undefined && v.lng !== undefined).map(v => [v.lat!, v.lng!] as L.LatLngTuple);
    if (points.length) map.current?.fitBounds(L.latLngBounds(points), { padding: [75, 90], maxZoom: 14 });
    else map.current?.setView(center, 12);
  };
  return <>
    <div ref={element} className="fleet-map-canvas" aria-label="Live fleet map" />
    {tileError && <div className="fleet-map-notice">Map tiles are unavailable. Vehicle lists remain accessible.</div>}
    <div className="fleet-map-tools" role="toolbar" aria-label="Map tools">
      <button onClick={fit} aria-label="Show the whole fleet" title="Show the whole fleet">⛶</button>
      <button onClick={() => setShowFacilities(value => !value)} aria-pressed={showFacilities} aria-label="Show facilities" title="Facilities">▱</button>
      <button onClick={() => setShowTrail(value => !value)} aria-pressed={showTrail} aria-label="Show selected vehicle trail" title="Trail since this page opened">⌁</button>
    </div>
    <div className="fleet-map-legend"><span><i className="running" />Running</span><span><i className="idle" />Idle</span><span><i className="halted" />Halted</span><span><i className="unknown" />Unreachable / unknown</span></div>
  </>;
}
